/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { identity, pickBy } from 'lodash';
import {
  CrudEditType,
  CrudListType,
  CrudTemplate,
  FormList,
  FormListField,
  ListColumns
} from '../../../organisms/crud/form-types';

import { TFunction } from 'react-i18next';
import { Config } from 'app/types/config';
import React from 'react';
import { pick } from 'lodash';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import moment from 'moment';

/**
 * Get value field based on config type.
 */

function getValueField(translate: TFunction, config: Config | undefined): FormListField {
  const configType = config?.type || 'string';

  switch (configType) {
    case 'enum':
      return {
        type: 'selectField',
        name: 'value',
        label: translate('common.labels.value'),
        rules: { required: true },
        options: config?.metadata?.options || []
      };

    case 'boolean':
      return {
        type: 'checkBoxField',
        name: 'value',
        label: translate('common.labels.value'),
        rules: undefined
      };

    case 'number':
      return {
        type: 'inputNumberField',
        name: 'value',
        label: translate('common.labels.value'),
        rules: { required: true }
      };

    case 'json':
      return {
        type: 'inputField',
        inputType: 'textArea',
        rows: 6,
        name: 'value',
        label: translate('common.labels.value'),
        rules: { required: true }
      };

    case 'string':
    default:
      return {
        type: 'inputField',
        name: 'value',
        label: translate('common.labels.value'),
        rules: { required: true }
      };
  }
}

/**
 * Dynamic form fields based on config type.
 */

const formFields = (translate: TFunction) => {
  return (config: Config | undefined): FormList => [
    {
      type: 'inputField',
      name: 'key',
      label: translate('common.labels.key'),
      disabled: true,
      rules: undefined
    },
    getValueField(translate, config),
    {
      type: 'inputField',
      inputType: 'textArea',
      rows: 2,
      name: 'description',
      label: translate('common.labels.description'),
      disabled: true,
      rules: undefined
    }
  ];
};

/**
 * Get display value for enum types.
 */

function getDisplayValue(config: Config): string {
  if (config.type === 'enum' && config.metadata?.options) {
    const option = config.metadata.options.find(opt => opt.value === config.value);

    return option?.label || config.value;
  }

  if (config.type === 'boolean') {
    return config.value === 'true' ? 'Yes' : 'No';
  }

  return config.value;
}

/**
 * List labels.
 */

const listColumns = (translate: TFunction): ListColumns<Config> => [
  {
    title: translate('common.table.columns.key'),
    dataIndex: 'key',
    key: 'key',
    size: '1fr'
  },
  {
    title: translate('common.table.columns.value'),
    dataIndex: 'value',
    key: 'value',
    size: '1fr',
    render: (config: Config) => getDisplayValue(config)
  },
  {
    title: translate('common.table.columns.description'),
    dataIndex: 'description',
    key: 'description',
    size: '1fr'
  },
  {
    title: translate('common.table.columns.type'),
    dataIndex: 'type',
    key: 'type',
    size: '100px'
  },
  {
    title: translate('common.table.columns.updatedAt'),
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    size: '180px',
    render: value => <DateTime date={value.updatedAt} />
  }
];

/**
 * List.
 */

function list(translate: TFunction): CrudListType<Config> {
  return {
    columns: listColumns(translate),
    route: appRoutes.dashboard.settings.config,
    canEdit: true,
    canRemove: false,
    enableExport: false,
    resolveParams: (params: any, item?: Config) => {
      return {
        id: params.id ?? item?.id
      };
    },
    key: ['config'],
    normalizeFilters: (data: any) => {
      const { ...filters } = data.filters;
      const result: any = {
        filters: pickBy(filters, identity),
        order: []
      };

      return result;
    },
    normalizeData: (result: any) => {
      return result;
    },
    pagination: {
      pageSize: 25,
      pageSizeOptions: [10, 25, 50, 100]
    },
    endpoint: apiEndpoints.config,
    endpointById: apiEndpoints.configById
  };
}

/**
 * Edit.
 */

function edit(translate: TFunction): CrudEditType<Config> {
  return {
    formFields: formFields(translate),
    endpoint: apiEndpoints.configById,
    route: appRoutes.dashboard.settings.configEdit,
    submitLabel: translate('common.actions.save'),
    normalizePayload: (values: any) => {
      return pick(values, ['value']);
    },
    normalizeInitialValues: (keys: string[], payload: Config) => {
      const data: any = pick(payload, keys);

      data.createdAt = moment(data.createdAt).format('YYYY-MM-DD HH:mm:ss');
      data.updatedAt = moment(data.updatedAt).format('YYYY-MM-DD HH:mm:ss');

      return data;
    }
  };
}

/**
 * Config.
 */

export function createConfigTemplate(translate: TFunction): CrudTemplate<Config> {
  return {
    list: list(translate),
    edit: edit(translate)
  };
}
